import { useCallback, useEffect, useState } from 'react';

import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { MODAL_AUTO_BRAIN } from '@/components/organisms/Modals/ModalConductor';
import useBrains from '@/hooks/useBrains';
import useDialogs from '@/hooks/useDialogs';
import { pushModal } from '@/redux/modals/actions';
import { selectAccountSlug, selectDialogId, selectBrainId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { autoDialogLanguages } from '@/util/constants';

import SubNav from './SubNav';
import { makeCollectionName, makeCollections } from './utils';

interface CollectionProps {
  collection: string;
  new_collection: string;
}

const SubNavDialog = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const dialogId = useSelector(selectDialogId);
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);
  const sessionDialogId = useSelector(selectDialogId);

  const {
    dialogs,
    createDraftDialog,
    updateCollection,
    updateDialog,
    listStatus,
    createStatus,
    isDraft,
    dialogDraft,
    detailedOptions,
  } = useDialogs(brainId, dialogId);
  const [lastCreatedCollection, setLastCreatedCollection] = useState<string>('');

  const getCollections = useCallback(() => {
    const startingNodeId = brain?.starting_node_id;
    const startingDialogId = detailedOptions.find(
      (option) => option.value === startingNodeId
    )?.dialog_id;

    let formattedDialogs =
      dialogs
        ?.map(({ dialog_id, name, collection, nodes }) => {
          const firstNode = nodes?.[0];
          const link = `/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialog_id}`;
          const isStartingDialog = dialog_id === startingDialogId;

          return {
            name: dialog_id === dialogDraft.dialog_id ? dialogDraft.name : name,
            id: dialog_id,
            collection,
            link,
            type: firstNode?.type,
            isStartingDialog,
          };
        })
        .sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        }) || [];

    if (isDraft) {
      const previousCollection = dialogs?.find((d) => d.dialog_id === sessionDialogId)?.collection;

      formattedDialogs = [
        {
          name: dialogDraft.name,
          id: 'draft',
          collection: previousCollection ?? '',
          link: `/${slug}/ai-agents/${brainId}/workflows/dialogs/draft`,
          type: null,
          isStartingDialog: false,
        },
        ...formattedDialogs,
      ];
    }
    const newCollections = makeCollections(formattedDialogs);
    return newCollections;
  }, [
    brain?.starting_node_id,
    brainId,
    detailedOptions,
    dialogDraft,
    dialogs,
    isDraft,
    sessionDialogId,
    slug,
  ]);

  const menuItems = [
    { label: t('dialog.create_empty_dialog'), value: 'manual' },
    { label: t('dialog.create_auto'), value: 'auto' },
  ];

  const isAutoBrainAvailable = autoDialogLanguages.includes(brain?.language);
  const createMenuItems = isAutoBrainAvailable ? menuItems : undefined;

  // create dialog
  const handleCreate = useCallback(() => {
    if (isAutoBrainAvailable) {
      trackEvent(EventName.ClickCreateManualDialog);
    }
    createDraftDialog();
  }, [createDraftDialog, isAutoBrainAvailable]);

  // update dialog
  const handleCollectionUpdate = useCallback(
    ({ id, collection }) => {
      updateDialog(
        { dialog_id: id, collection },
        {
          onSuccess: (resp) => {
            toast.success(t('dialog.dialog_updated', { 0: resp.name }));
            navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${resp.dialog_id}`);
          },
        }
      );
    },
    [brainId, navigate, slug, t, updateDialog]
  );

  const onCreateCollection = useCallback(
    ({ id }) => {
      const collectionName = makeCollectionName(getCollections());
      updateDialog(
        { dialog_id: id, collection: collectionName },
        {
          onSuccess: () => {
            setLastCreatedCollection(collectionName);
          },
        }
      );
    },
    [getCollections, updateDialog]
  );

  useEffect(() => {
    if (brain?.language) {
      i18next.loadLanguages([brain.language]).catch((error) => {
        console.warn(
          `Could not load translations for ${brain.language}, falling back to English`,
          error
        );
      });
    }
  }, [brain?.language]);

  const onCollectionSubmit = useCallback(
    (data: CollectionProps) => {
      updateCollection(data);
    },
    [updateCollection]
  );

  const createMenuClick = (value: 'auto' | 'manual') => {
    if (value === 'auto') {
      dispatch(pushModal(MODAL_AUTO_BRAIN));
      trackEvent(EventName.ClickCreateManualDialog);
    } else {
      handleCreate();
    }
  };

  return (
    <SubNav
      title={t('common.dialog')}
      navTitle={t('common.dialogs')}
      skeleton={listStatus === 'pending'}
      collections={getCollections()}
      onCreateClick={handleCreate}
      onCollectionUpdate={handleCollectionUpdate}
      onCreateCollection={onCreateCollection}
      onCollectionSubmit={onCollectionSubmit}
      menuItems={createMenuItems}
      createMenuClick={createMenuClick}
      lastCreatedCollection={lastCreatedCollection}
      isCreating={createStatus === 'pending'}
      whoIsViewing={[]}
      subnavType="dialogs"
    />
  );
};

export default SubNavDialog;
